// deps
import React, { ReactElement, useCallback } from "react";

// helpers
import { getCloudinaryUrl } from "@swiggy-private/js-utils";

// dls
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";

// interfaces
import { BannerData } from "@payment-ui/interfaces/PaymentBanner";

// styled components
import {
    NCEMIBannerFooter,
    NCEMIBannerContainer,
    NCEMIBannerContent,
    NCEMIBannerContentLeft,
    NCEMIButtonWrapper,
    DisableText,
    DisableIcon,
} from "../styles";

interface NCEMIBannerProps {
    banner: BannerData;
    handleClick: (e: React.MouseEvent) => void;
}

const imageDimensions = {
    height: 246,
};

export function NCEMIBanner(props: NCEMIBannerProps): ReactElement {
    const { banner, handleClick } = props;
    const isEnabled = banner.enabled;

    const handleOnClick = useCallback(
        (e: React.MouseEvent) => {
            if (isEnabled) {
                handleClick(e);
            }
        },
        [isEnabled, handleClick],
    );

    const renderButton = useCallback(() => {
        if (!banner.cta?.text) return null;

        return (
            <NCEMIButtonWrapper>
                <SuperButton
                    variant="small"
                    buttonSize="small"
                    buttonType="specialPositive"
                    isDisabled={!isEnabled}
                    onClick={handleOnClick}
                >
                    {banner.cta?.text}
                </SuperButton>
            </NCEMIButtonWrapper>
        );
    }, [banner, isEnabled, handleOnClick]);

    const renderDisableText = useCallback(() => {
        if (!banner.disabledText) return null;

        return (
            <NCEMIBannerFooter>
                <DisableIcon>
                    <SpriteIconWeb iconName="warningFilled16" fillColor="warning" strokeColor="warning" />
                </DisableIcon>
                <DisableText variant="V2.overline.neutral.medium" color="warning">
                    {banner.disabledText}
                </DisableText>
            </NCEMIBannerFooter>
        );
    }, [banner]);

    return (
        <NCEMIBannerContainer $disabled={!isEnabled}>
            <NCEMIBannerContent
                $disabled={!isEnabled}
                $backgroundImage={getCloudinaryUrl(banner.backgroundImage, {
                    ...imageDimensions,
                })}
            >
                <NCEMIBannerContentLeft>
                    <Text variant="V2.body2.neutral.bold" color="text_High_Emphasis">
                        {banner.title}
                    </Text>
                    <Text variant="V2.body3.neutral.regular" color="text_Low_Emphasis">
                        {banner.description}
                    </Text>
                    {renderButton()}
                </NCEMIBannerContentLeft>
            </NCEMIBannerContent>
            {renderDisableText()}
        </NCEMIBannerContainer>
    );
}
